<template>
  <gg-card>
    <gg-card-header>
      <v-row>
        <v-col md="6">
          Your Packages
        </v-col>
        <v-col md="6" class="text-right font-sm">
          <gg-btn :disabled="!featureListingAllowed" color="primary" @click="openAddEditModal()">
            Add Package
          </gg-btn>
        </v-col>
      </v-row>
    </gg-card-header>
    <gg-card-text v-if="!featureListingAllowed">
      <v-row>
        <v-col>
          You don't have enough permissions for this action
        </v-col>
      </v-row>
    </gg-card-text>
    <gg-card-text v-else>
      <v-data-table :items="packageList"
                    :loading="loadingPackages"
                    :headers="headers"
      >
        <template v-slot:item.actions="{ item }">
          <gg-btn :disabled="!!item.suspended" class="mr-2" :to="{ path: `/hosting/shared/${hostingid}`, query: { user: item.user } }">
            Manage
          </gg-btn>
        </template>
        <template v-slot:item.manage="{ item }">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <gg-btn color="primary" v-bind="attrs" v-on="on">
                Manage <v-icon right>mdi-chevron-down</v-icon>
              </gg-btn>
            </template>
            <v-list>
              <v-list-item @click="openAddEditModal(item.name)">
                <v-list-item-title>
                  <v-icon small class="pb-1">mdi-pencil</v-icon>
                  <span>
                    Edit
                  </span>
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="deletePackage(item.name)">
                <v-list-item-title>
                  <v-icon small class="pb-1">mdi-trash-can-outline</v-icon>
                  Delete
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </gg-card-text>

    <gg-modal :is-active="isShowPackageModal" @close="isShowPackageModal=false">
      <template v-slot:toolbar>
        {{ isAdding ? 'Add' : 'Edit' }} a Package
      </template>
      <template v-slot:body>
        <v-row class="pt-3">
          <v-col cols="12">
            <v-row>
              <v-col cols="12" class="py-0">
                <gg-text-field
                  outlined
                  :disabled="!isAdding"
                  label="Package Name"
                  v-model="formData.packageName"
                  v-validate="rules.packageName"
                  data-vv-name="packageName"
                  data-vv-as="Package Name"
                  :error-messages="errors.collect('packageName')"
                ></gg-text-field>
              </v-col>
            </v-row>
            <gg-card-header class="px-0" :divider="false">
              Resources
            </gg-card-header>
            <v-row class="mt-3">
              <v-col cols="12" class="py-0">
                <gg-text-field
                  outlined
                  label="Disk Space Quota (MB)"
                  v-model="formData.resources.quota"
                  v-validate="rules.quota"
                  data-vv-name="quota"
                  data-vv-as="Disk Space Quota"
                  :error-messages="errors.collect('quota')"
                ></gg-text-field>
              </v-col>
              <v-col cols="12" class="py-0">
                <gg-text-field
                  outlined
                  label="Monthly Bandwidth Limit (MB)"
                  v-model="formData.resources.bwlimit"
                  v-validate="rules.bwlimit"
                  data-vv-name="bwlimit"
                  data-vv-as="Monthly Bandwidth Limit"
                  :error-messages="errors.collect('bwlimit')"
                ></gg-text-field>
              </v-col>
              <v-col cols="12" class="py-0">
                <gg-text-field
                  outlined
                  label="Max FTP Accounts"
                  v-model="formData.resources.maxftp"
                  v-validate="rules.maxftp"
                  data-vv-name="maxftp"
                  data-vv-as="Max FTP Accounts"
                  :error-messages="errors.collect('maxftp')"
                ></gg-text-field>
              </v-col>
              <v-col cols="12" class="py-0">
                <gg-text-field
                  outlined
                  label="Max Email Accounts"
                  v-model="formData.resources.maxpop"
                  v-validate="rules.maxpop"
                  data-vv-name="maxpop"
                  data-vv-as="Max Email Accounts"
                  :error-messages="errors.collect('maxpop')"
                ></gg-text-field>
              </v-col>
              <v-col cols="12" class="py-0">
                <gg-text-field
                  outlined
                  label="Max Mailing Lists"
                  v-model="formData.resources.maxlst"
                  v-validate="rules.maxlst"
                  data-vv-name="maxlst"
                  data-vv-as="Max Mailing Lists"
                  :error-messages="errors.collect('maxlst')"
                ></gg-text-field>
              </v-col>
              <v-col cols="12" class="py-0">
                <gg-text-field
                  outlined
                  label="Max SQL Databases"
                  v-model="formData.resources.maxsql"
                  v-validate="rules.maxsql"
                  data-vv-name="maxsql"
                  data-vv-as="Max SQL Databases"
                  :error-messages="errors.collect('maxsql')"
                ></gg-text-field>
              </v-col>
              <v-col cols="12" class="py-0">
                <gg-text-field
                  outlined
                  label="Max Sub Domains"
                  v-model="formData.resources.maxsub"
                  v-validate="rules.maxsub"
                  data-vv-name="maxsub"
                  data-vv-as="Max Sub Domains"
                  :error-messages="errors.collect('maxsub')"
                ></gg-text-field>
              </v-col>
              <v-col cols="12" class="py-0">
                <gg-text-field
                  outlined
                  label="Max Parked Domains"
                  v-model="formData.resources.maxpark"
                  v-validate="rules.maxpark"
                  data-vv-name="maxpark"
                  data-vv-as="Max Parked Domains"
                  :error-messages="errors.collect('maxpark')"
                ></gg-text-field>
              </v-col>
              <v-col cols="12" class="py-0">
                <gg-text-field
                  outlined
                  label="Max Addon Domains"
                  v-model="formData.resources.maxaddon"
                  v-validate="rules.maxaddon"
                  data-vv-name="maxaddon"
                  data-vv-as="Max Addon Domains"
                  :error-messages="errors.collect('maxaddon')"
                ></gg-text-field>
              </v-col>
              <v-col cols="12" class="py-0">
                <gg-text-field
                  outlined
                  label="Max Passenger Applications"
                  v-model="formData.resources.maxpassengerapps"
                  v-validate="rules.maxpassengerapps"
                  data-vv-name="maxpassengerapps"
                  data-vv-as="Max Passenger Applications"
                  :error-messages="errors.collect('maxpassengerapps')"
                ></gg-text-field>
              </v-col>
              <v-col cols="12" class="py-0">
                <gg-text-field
                  outlined
                  disabled="disabled"
                  label="Maximum Hourly Email by Domain Relayed"
                  v-model="formData.resources.max_email_per_hour"
                  v-validate="rules.max_email_per_hour"
                  data-vv-name="max_email_per_hour"
                  data-vv-as="Maximum Hourly Email"
                  :error-messages="errors.collect('max_email_per_hour')"
                ></gg-text-field>
              </v-col>
              <v-col cols="12" class="py-0">
                <gg-text-field
                  outlined
                  disabled="disabled"
                  label="Maximum percentage of failed or deferred messages a domain may send per hour"
                  v-model="formData.resources.max_defer_fail_percentage"
                  v-validate="rules.max_defer_fail_percentage"
                  data-vv-name="max_defer_fail_percentage"
                  data-vv-as="Maximum percentage of failed or deferred messages per hour"
                  :error-messages="errors.collect('max_defer_fail_percentage')"
                ></gg-text-field>
              </v-col>
              <v-col cols="12" class="py-0">
                <gg-text-field
                  outlined
                  label="Max Quota per Email Address (MB)"
                  v-model="formData.resources.max_emailacct_quota"
                  v-validate="rules.max_emailacct_quota"
                  data-vv-name="max_emailacct_quota"
                  data-vv-as="Max Quota per Email Address"
                  :error-messages="errors.collect('max_emailacct_quota')"
                ></gg-text-field>
              </v-col>
            </v-row>
            <gg-card-header class="px-0" :divider="false">
              Settings
            </gg-card-header>
            <v-row class="mt-3">
              <v-col cols="12" class="py-0">
                <gg-select
                  outlined
                  label="cPanel Theme"
                  v-model="formData.settings.cpmod"
                  :items="themes"
                ></gg-select>
              </v-col>
              <v-col cols="12" class="py-0">
                <gg-select
                  outlined
                  label="Feature List"
                  v-model="formData.settings.featurelist"
                  :items="featureLists"
                ></gg-select>
              </v-col>
              <v-col cols="12" class="py-0">
                <gg-select
                  outlined
                  label="Locale"
                  v-model="formData.settings.language"
                  v-validate="rules.language"
                  :items="[{label: 'English', value: 'en'}]"
                  item-value="value"
                  item-text="label"
                  data-vv-name="language"
                  data-vv-as="language"
                  :error-messages="errors.collect('language')"
                ></gg-select>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </template>
      <template v-slot:actions>
        <gg-btn primary @click="save">
          {{ isAdding ? 'Add' : 'Save Changes' }}
        </gg-btn>
        <gg-btn v-if="!isAdding" secondary @click="reset(formData.packageName)">
          Reset
        </gg-btn>
      </template>
    </gg-modal>
  </gg-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  RESELLER_SERVICE_PERMANENT_FEATURE_LISTS,
  RESELLER_SERVICE_PRIVILEGE_PACKAGE_ADD,
} from '@/consts/service'

export default {
  name: 'ResellerPackages',

  data () {
    return {
      headers: [
        {
          text: 'Package Name',
          value: 'name',
        },
        {
          text: 'Manage',
          value: 'manage',
          align: 'center',
          sortable: false,
        }
      ],
      formData: {
        packageName: '',
        resources: {
          quota: '',
          bwlimit: '',
          maxftp: '',
          maxpop: '',
          maxlst: '',
          maxsql: '',
          maxsub: '',
          maxpark: '',
          maxaddon: '',
          maxpassengerapps: '',
          max_email_per_hour: '',
          max_defer_fail_percentage: '',
          max_emailacct_quota: '',
        },
        settings: {
          ip: false,
          hasshell: false,
          cgi: false,
          digestauth: false,
          cpmod: '',
          featurelist: '',
          language: 'en',
        },
      },
      formDataString: '',
      loadingPackages: false,
      isShowPackageModal: false,
      isAdding: true,
      rules: {
        packageName: { required: true },
        quota: { required: true, min_value: 1 },
        bwlimit: { required: true, min_value: 1 },
        maxftp: { required: true, min_value: 0 },
        maxpop: { required: true, min_value: 0 },
        maxlst: { required: true, min_value: 0 },
        maxsql: { required: true, min_value: 0 },
        maxsub: { required: true, min_value: 0 },
        maxpark: { required: true, min_value: 0 },
        maxaddon: { required: true, min_value: 0 },
        maxpassengerapps: { required: true, min_value: 0 },
        max_emailacct_quota: { required: true, min_value: 1 },
      },
      featureLists: [],
      themes: [],
      packagesBeforeAdd: 0,
    }
  },
  computed: {
    ...mapGetters(['getResellerPackages', 'getResellerFeatureLists', 'getResellerThemes', 'getResellerPrivileges']),
    hostingid () {
      return this.hosting.id
    },
    packageList () {
      return this.getResellerPackages.map((pack) => {
        return {
          name: pack.name,
          featurelist: pack.FEATURELIST,
        }
      })
    },
    featureListingAllowed () {
      return !!this.getResellerPrivileges[RESELLER_SERVICE_PRIVILEGE_PACKAGE_ADD]
    },
  },

  methods: {
    ...mapActions([
      'fetchResellerPackages',
      'fetchResellerFeatureLists',
      'fetchResellerThemes',
      'createResellerPackage',
      'deleteResellerPackage',
      'updateResellerPackage',
    ]),
    resetFormData (packageName) {
      const dataToSet = packageName
        ? JSON.parse(this.formDataString)
        : {
          packageName: '',
          resources: {
            quota: '1024',
            bwlimit: '1048576',
            maxftp: '0',
            maxpop: '0',
            maxlst: '0',
            maxsql: '0',
            maxsub: '0',
            maxpark: '0',
            maxaddon: '0',
            maxpassengerapps: '4',
            max_email_per_hour: '100',
            max_defer_fail_percentage: '100',
            max_emailacct_quota: '1024',
          },
          settings: {
            ip: false,
            hasshell: false,
            cgi: true,
            digestauth: false,
            cpmod: 'jupiter',
            featurelist: 'default',
            language: 'en',
          },
        }
      Object.assign(this.formData, dataToSet)
    },
    getPackageDataByName (packageName) {
      const pack = this.getResellerPackages.find((pack) => pack.name === packageName)
      if (!pack) {
        throw new Error(`Can not find package "${packageName}"`)
      }
      return {
        packageName,
        resources: {
          quota: pack.QUOTA,
          bwlimit: pack.BWLIMIT,
          maxftp: pack.MAXFTP,
          maxpop: pack.MAXPOP,
          maxlst: pack.MAXLST,
          maxsql: pack.MAXSQL,
          maxsub: pack.MAXSUB,
          maxpark: pack.MAXPARK,
          maxaddon: pack.MAXADDON,
          maxpassengerapps: pack.MAXPASSENGERAPPS,
          max_email_per_hour: pack.MAX_EMAIL_PER_HOUR,
          max_defer_fail_percentage: pack.MAX_DEFER_FAIL_PERCENTAGE,
          max_emailacct_quota: pack.MAX_EMAILACCT_QUOTA,
        },
        settings: {
          ip: pack.IP === 'y',
          hasshell: pack.HASSHELL === 'y',
          cgi: pack.CGI === 'y',
          digestauth: pack.DIGESTAUTH === 'y',
          cpmod: pack.CPMOD,
          featurelist: pack.FEATURELIST,
          language: pack.LANG,
        }
      }
    },
    snapshotFormData (packageName) {
      this.formDataString = JSON.stringify(this.getPackageDataByName(packageName))
    },
    openAddEditModal (packageName) {
      if (packageName) {
        this.isAdding = false
        this.snapshotFormData(packageName)
        this.resetFormData(packageName)
      } else {
        this.isAdding = true
        this.resetFormData()
      }
      this.isShowPackageModal = true
    },
    setFeatureLists () {
      this.featureLists.push(...RESELLER_SERVICE_PERMANENT_FEATURE_LISTS, ...this.getResellerFeatureLists)
    },
    setThemes () {
      this.themes.push(...this.getResellerThemes)
    },
    async save () {
      const isValid = await this.$validator.validateAll()
      if (!isValid) {
        return
      }
      if (this.isAdding) {
        const isSuccess = await this.createResellerPackage(this.getFormattedFormDataForRequest())
        if (isSuccess !== false) {
          this.fetchPackagesWithLoop()
        }
      } else {
        const isSuccess = await this.updateResellerPackage(this.getFormattedFormDataForRequest())
        if (isSuccess !== false) {
          this.fetchPackages()
        }
      }
      this.isShowPackageModal = false
      this.resetFormData()
    },
    async fetchPackages () {
      this.loadingPackages = true
      setTimeout(async () => {
        await this.fetchResellerPackages()
        this.loadingPackages = false
      }, 500)
    },
    async fetchPackagesWithLoop (attempt = 1) {
      this.loadingPackages = true
      try {
        await this.fetchResellerPackages()
      } catch (e) {
        this.loadingPackages = false
      }
      if (attempt === 10) {
        this.loadingPackages = false
        return
      }
      if (this.packagesBeforeAdd !== this.packageList.length) {
        this.packagesBeforeAdd = this.packageList.length
        this.loadingPackages = false
        return
      }
      setTimeout(() => {
        this.fetchPackagesWithLoop(attempt + 1)
      }, 500 * attempt)
    },
    reset (packageName) {
      this.resetFormData(packageName)
    },
    async deletePackage (packageName) {
      const isSuccess = await this.deleteResellerPackage({ pkgname: packageName })
      if (isSuccess !== false) {
        this.fetchPackagesWithLoop()
      }
    },
    getFormattedFormDataForRequest () {
      const {
        packageName,
        resources,
        settings,
      } = this.formData
      settings.cgi = settings.cgi ? 1 : 0
      settings.digestauth = settings.digestauth ? 1 : 0
      return {
        name: packageName,
        ...resources,
        ...settings,
      }
    },
    async boot () {
      if (!this.featureListingAllowed) {
        return
      }
      this.loadingPackages = true
      await Promise.all([
        this.fetchResellerPackages(),
        this.fetchResellerFeatureLists(),
        this.fetchResellerThemes({ id: this.$route.params.hostingid }),
      ])
      this.loadingPackages = false
      this.resetFormData()
      this.setFeatureLists()
      this.setThemes()
      this.packagesBeforeAdd = this.packageList.length
    }

  },
  watch: {
    getResellerPrivileges () {
      this.boot()
    }
  },
  created () {
    this.boot()
  }
}
</script>
